import React, { FC, useEffect, useState } from 'react'
import { Col, DatePicker, Row, Select, Spin } from 'antd'
import { StyledCollapse } from '../styles'
import moment from 'moment'
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  LegendType,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import RChartTooltip from '../../../../components/Charts/RChartTooltip'
import { useTheme } from 'styled-components'
import { modelApi } from '../../../../api'
import { RangePickerProps } from 'antd/es/date-picker'
import { IGenerationEnriched } from '../../../../types/generation'
import { formatMomentToApiDate } from '../../../../utils'
import { RANGE_TYPES } from '../../../../constants'

type Props = {
  ids: string[]
  chargeDataId: string
  referenceLine: number
}

const GenerationBarChart: FC<Props> = ({ ids, chargeDataId, referenceLine }) => {
  const theme = useTheme()
  const [data, setData] = useState<IGenerationEnriched>([])
  const [isLoading, setLoading] = useState(true)
  const [rangeType, setRangeType] = useState<string>(RANGE_TYPES.HOUR)
  const [closedDataKeys, setClosedDataKeys] = useState<string[]>([])
  const [range, setRange] = useState<RangePickerProps['value']>([
    moment('2020-01-01 00:00:00').startOf('month'),
    moment('2020-03-01 00:00:00').endOf('month'),
  ])

  async function fetchGenerationData() {
    try {
      setLoading(true)
      const startDate = formatMomentToApiDate(range?.[0] || moment())
      const endDate = formatMomentToApiDate(range?.[1] || moment())
      console.log(ids)
      const { charge, discharge } = await modelApi.fetchBatteryStates({
        id: chargeDataId,
        startDate,
        endDate,
        range: rangeType,
      })
      const data = await Promise.all(
        ids.map((id) => modelApi.fetchGenerationData({ id, startDate, endDate, range: rangeType })),
      )

      const dates = Object.keys(charge || {})
      const charges = Object.values(charge || {})
      const discharges = Object.values(discharge || {})
      const productionData = data.map((generation) => Object.values(generation.gen))

      const mergedProductionData = dates.map((date, idx) => ({
        date,
        charge: charges[idx],
        discharge: discharges[idx],
        ...productionData
          .map((pd) => parseFloat(pd[idx].toFixed(2)))
          .reduce((prev, cur, idx) => ({ ...prev, [`GEN-${idx + 1}`]: cur }), {}),
      }))

      setData(mergedProductionData)
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }

  function toggleClosedKeys(dataKey = '') {
    if (closedDataKeys.includes(dataKey)) {
      setClosedDataKeys(closedDataKeys.filter((dk) => dk !== dataKey))
    } else {
      setClosedDataKeys([...closedDataKeys, dataKey])
    }
  }

  useEffect(() => {
    fetchGenerationData()
  }, [range, rangeType])
  console.log(closedDataKeys)
  return (
    <Spin spinning={isLoading}>
      <StyledCollapse className='mb-24' defaultActiveKey={1}>
        <StyledCollapse.Panel header='Time Series Analysis' key={1}>
          <Row className='mb-8' justify='end' gutter={8}>
            <Col>
              <Select
                value={rangeType}
                options={Object.entries(RANGE_TYPES).map(([key, type]) => ({
                  label: key,
                  value: type,
                }))}
                onChange={setRangeType}
              />
            </Col>
            <Col>
              <DatePicker.RangePicker value={range} onChange={(r) => setRange(r)} />
            </Col>
          </Row>
          <ResponsiveContainer width='100%' height={400}>
            <ComposedChart
              width={500}
              height={400}
              data={data}
              margin={{ left: theme.spacing.spacing24 }}
            >
              {/* TODO: REFACTOR -> GENERIC CHART */}
              <Legend
                payload={[
                  ...ids.map((_, idx) => ({
                    value: `GEN-${idx + 1}`,
                    dataKey: `GEN-${idx + 1}`,
                    type: 'line' as LegendType,
                    color: theme.rainbow[idx],
                    inactive: closedDataKeys.includes(`GEN-${idx + 1}`),
                  })),
                  {
                    value: 'Charge',
                    dataKey: 'charge',
                    type: 'line',
                    color: theme.palettes.green[5],
                    inactive: closedDataKeys.includes('charge'),
                  },
                  {
                    value: 'Discharge',
                    dataKey: 'discharge',
                    type: 'line',
                    color: theme.palettes.red[5],
                    inactive: closedDataKeys.includes('discharge'),
                  },
                ]}
                onClick={({ dataKey }) => toggleClosedKeys(dataKey)}
              />
              <CartesianGrid strokeDasharray='3 3' stroke='#f5f5f5' />
              <ReferenceLine
                y={referenceLine}
                label='60 MW'
                stroke={theme.colors.dark}
                strokeDasharray='10'
              />
              <XAxis
                tickFormatter={(date: string) => moment(date).format('DD/MM/YYYY')}
                minTickGap={20}
                dataKey='date'
                label={{ value: 'Time', position: 'insideBottomRight', offset: 0 }}
              />
              <YAxis domain={[0, 'auto']} unit='MWh' />
              <Tooltip content={<RChartTooltip />} />
              {ids.map(
                (_, idx) =>
                  !closedDataKeys.includes(`GEN-${idx + 1}`) && (
                    <Area
                      key={idx}
                      stackId='1'
                      type='monotone'
                      dataKey={`GEN-${idx + 1}`}
                      fill={theme.rainbow[idx]}
                      stroke={theme.rainbow[idx]}
                    />
                  ),
              )}

              {!closedDataKeys.includes('charge') && (
                <Area
                  type='monotone'
                  dataKey='charge'
                  fill={theme.palettes.green[5]}
                  stroke={theme.palettes.green[7]}
                  strokeWidth={2}
                  strokeDasharray='5'
                />
              )}
              {!closedDataKeys.includes('discharge') && (
                <Area
                  type='monotone'
                  dataKey='discharge'
                  strokeDasharray='5'
                  fillOpacity={0.1}
                  fill={theme.palettes.red[4]}
                  stroke={theme.palettes.red[5]}
                  strokeWidth={2}
                />
              )}

              <Bar dataKey='chargeAbs'>
                {/* {composedData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.charge > 0 ? theme.palettes.green[3] : theme.palettes.red[3]}
              stroke={entry.charge > 0 ? theme.colors.green : theme.colors.red}
            />
          ))} */}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </StyledCollapse.Panel>
      </StyledCollapse>
    </Spin>
  )
}

export default GenerationBarChart
